import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaStar, FaSwimmingPool, FaWifi, FaUtensils, FaFire, FaGamepad, FaSnowflake, FaBed, FaHotTub, FaTv, FaWalking, FaPercent, FaCheckCircle } from 'react-icons/fa';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';

interface Service {
    id: string;
    name: string;
    description: string;
    price: number;
}

const Home: React.FC = () => {
    const [services, setServices] = useState<Service[]>([]);

    const [roomTypes, setRoomTypes] = useState([
        {
            name: 'Suite Room',
            originalPrice: 6000,
            price: 6000,
            description: 'Luxurious suite with premium amenities and spacious layout',
            features: ['King Size Bed', 'Private Balcony', 'Premium AC', 'Mini Fridge'],
            image: '/images/IMG_20251210_175845.jpg'
        },
        {
            name: 'Single Room',
            originalPrice: 2500,
            price: 2500,
            description: 'Comfortable single occupancy room with all essential amenities',
            features: ['Queen Size Bed', 'AC', 'WiFi', 'Hot Water'],
            image: '/images/IMG_20251210_180546.jpg'
        }
    ]);

    useEffect(() => {
        const fetchServices = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'services'));
                const servicesData = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as Service[];
                setServices(servicesData.slice(0, 3));
            } catch (error) {
                console.error('Error fetching services:', error);
            }
        };

        const fetchRoomTypes = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'roomTypes'));
                const roomTypesData = querySnapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        name: data.name,
                        originalPrice: data.originalPrice,
                        price: data.discountedPrice,
                        description: data.description,
                        features: data.features || [],
                        image: data.name === 'Suite Room' ? '/images/IMG_20251210_175845.jpg' : '/images/IMG_20251210_180546.jpg'
                    };
                });
                
                // Filter only active room types
                const activeRoomTypes = roomTypesData.filter((_, index, arr) => {
                    const doc = querySnapshot.docs[index];
                    return doc.data().isActive !== false;
                });
                
                if (activeRoomTypes.length > 0) {
                    setRoomTypes(activeRoomTypes);
                }
            } catch (error) {
                console.error('Error fetching room types:', error);
                // Keep default room types if Firebase fails
            }
        };

        fetchServices();
        fetchRoomTypes();
    }, []);

    const amenities = [
        { icon: FaSwimmingPool, name: 'Unlimited Pool Access', description: 'Dive into our crystal-clear swimming pool anytime' },
        { icon: FaFire, name: 'Campfire', description: 'Cozy evening campfire sessions under the stars' },
        { icon: FaGamepad, name: 'Play Area', description: 'Fun-filled play area for kids and families' },
        { icon: FaSnowflake, name: 'Air Conditioning', description: 'Climate-controlled comfort in all rooms' },
        { icon: FaWifi, name: 'Free WiFi', description: 'High-speed internet throughout the resort' },
        { icon: FaBed, name: 'Extra Beds', description: 'Additional bedding available on request' },
        { icon: FaHotTub, name: 'Hot Water', description: '24/7 hot water supply in all rooms' },
        { icon: FaUtensils, name: 'Food Delivery', description: 'On-demand food from selected restaurants' },
        { icon: FaTv, name: 'Television', description: 'Entertainment with cable TV in every room' },
        { icon: FaWalking, name: 'Walk Area', description: 'Scenic walking paths through our gardens' }
    ];

    return (
        <div className="min-h-screen bg-background">
            {/* Hero Section */}
            <section className="relative h-screen flex items-center justify-center overflow-hidden">
                <div 
                    className="absolute inset-0 bg-cover bg-no-repeat"
                    style={{ 
                        backgroundImage: "url('/images/crop_bg.jpg')",
                        backgroundPosition: 'center center'
                    }}
                >
                </div>
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
                
                <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
                    <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                        <span className="block text-white mb-2">Sunrise</span>
                        <span className="block">Agri Resorts</span>
                    </h1>
                    <div className="backdrop-blur-md bg-white/10 rounded-3xl p-8 mb-8 border border-white/20 shadow-2xl">
                        <p className="text-xl md:text-2xl text-white max-w-2xl mx-auto leading-relaxed font-medium">
                            Where Nature Meets Luxury - Experience the perfect blend of comfort and tranquility
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Link 
                            to="/book-room" 
                            className="bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-full font-semibold text-base transition-all duration-300 shadow-2xl hover:shadow-xl transform hover:-translate-y-1 backdrop-blur-sm border border-primary/20"
                        >
                            Book Your Stay
                        </Link>
                        <Link 
                            to="/gallery" 
                            className="bg-white/10 border-2 border-white/60 text-white hover:bg-white hover:text-gray-900 px-6 py-3 rounded-full font-semibold text-base transition-all duration-300 backdrop-blur-md hover:backdrop-blur-none"
                        >
                            View Gallery
                        </Link>
                    </div>
                </div>
            </section>

            <main className="container mx-auto px-4">
                {/* Room Types & Pricing */}
                <section className="py-16">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold text-text mb-4">Our Rooms & Pricing</h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">Choose from our comfortable accommodations designed for your perfect stay</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                        {roomTypes.map((room, index) => (
                            <div key={index} className="bg-white rounded-3xl shadow-large overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                                {/* Image Section */}
                                <div className="relative h-64 overflow-hidden">
                                    <img 
                                        src={room.image} 
                                        alt={room.name}
                                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                                        style={room.name === 'Single Room' ? { objectPosition: '50% 80%' } : {}}
                                    />

                                </div>
                                
                                {/* Content Section */}
                                <div className="p-8">
                                    {/* Header with pricing */}
                                    <div className="flex justify-between items-start mb-6">
                                        <div>
                                            <h3 className="text-2xl font-bold text-text mb-2">{room.name}</h3>
                                            <p className="text-gray-600">{room.description}</p>
                                        </div>
                                        <div className="text-right ml-4">
                                            <div className="flex items-center justify-end space-x-2 mb-1">
                                                <span className="text-3xl font-bold text-primary">₹{room.price.toLocaleString()}</span>
                                            </div>
                                            <p className="text-sm text-gray-600">per night</p>
                                        </div>
                                    </div>

                                    {/* Features */}
                                    <div className="grid grid-cols-2 gap-3 mb-8">
                                        {room.features.map((feature, idx) => (
                                            <div key={idx} className="flex items-center space-x-2">
                                                <FaCheckCircle className="text-primary text-sm flex-shrink-0" />
                                                <span className="text-sm text-gray-700">{feature}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Book Now Button */}
                                    <Link 
                                        to="/book-room" 
                                        className="w-full bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary text-white py-4 rounded-full font-semibold text-lg transition-all duration-300 block text-center shadow-medium hover:shadow-large transform hover:-translate-y-0.5"
                                    >
                                        Book Now
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Amenities Section */}
                <section className="py-16 bg-gradient-to-br from-primary/5 to-accent/5 rounded-3xl mb-16">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold text-text mb-4">World-Class Amenities</h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">Everything you need for a memorable and comfortable stay</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 px-8">
                        {amenities.map((amenity, index) => (
                            <div key={index} className="bg-white p-6 rounded-xl shadow-soft hover:shadow-medium transition-all duration-300 text-center group hover:-translate-y-1">
                                <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-dark rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                                    <amenity.icon className="text-2xl text-white" />
                                </div>
                                <h3 className="font-semibold text-text mb-2">{amenity.name}</h3>
                                <p className="text-sm text-gray-600 leading-relaxed">{amenity.description}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Gallery Preview */}
                <section className="py-16">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold text-text mb-4">Resort Gallery</h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">Take a glimpse of our beautiful resort and facilities</p>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                        {[
                            { src: '/images/IMG_20251210_174329.jpg', alt: 'Resort Exterior View' },
                            { src: '/images/IMG_20251210_174421.jpg', alt: 'Swimming Pool Area' },
                            { src: '/images/IMG_20251210_175521.jpg', alt: 'Garden Landscape' },
                            { src: '/images/IMG_20251210_180546.jpg', alt: 'Resort Facilities' },
                            { src: '/images/IMG_20251210_175845.jpg', alt: 'Outdoor Seating' },
                            { src: '/images/IMG_20251210_173811.jpg', alt: 'Resort Ambiance' }
                        ].map((image, index) => (
                            <Link key={index} to="/gallery" className="block">
                                <div className="relative aspect-[4/3] overflow-hidden rounded-xl shadow-medium group cursor-pointer">
                                    <img 
                                        src={image.src} 
                                        alt={image.alt}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    />
                                </div>
                            </Link>
                        ))}
                    </div>
                    
                    <div className="text-center">
                        <Link 
                            to="/gallery" 
                            className="inline-flex items-center bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-full font-semibold transition-colors duration-200"
                        >
                            View Full Gallery
                        </Link>
                    </div>
                </section>

                {/* Guest Reviews Section */}
                <section className="py-16 bg-gradient-to-br from-blue-50 to-green-50 rounded-3xl mb-16">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold text-text mb-4">What Our Guests Say</h2>
                        <div className="flex items-center justify-center mb-4">
                            <div className="flex text-yellow-500 text-2xl mr-3">
                                <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                            </div>
                            <span className="text-2xl font-bold text-gray-800">4.9/5</span>
                        </div>
                        <p className="text-lg text-gray-700 mb-2">Based on 20+ authentic Google reviews</p>
                        <p className="text-sm text-gray-600">Verified guests who stayed at Sunrise Agri Resorts</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 px-8">
                        <div className="bg-white p-6 rounded-xl shadow-soft border-l-4 border-primary hover:shadow-medium transition-all duration-300 hover:-translate-y-1">
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <p className="font-bold text-gray-800">Ayyappa Srinu</p>
                                    <p className="text-sm text-gray-500">2 weeks ago</p>
                                </div>
                                <div className="flex text-yellow-500">
                                    <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                                </div>
                            </div>
                            <p className="text-gray-700 italic leading-relaxed">"Good experience and joyful place, thank you Sunrise Agri Resorts team"</p>
                            <div className="mt-3 flex items-center text-sm text-blue-600">
                                <span className="bg-blue-100 px-2 py-1 rounded-full">Google Review</span>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-xl shadow-soft border-l-4 border-primary hover:shadow-medium transition-all duration-300 hover:-translate-y-1">
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <p className="font-bold text-gray-800">Venkata Ramu</p>
                                    <p className="text-sm text-gray-500">2 weeks ago</p>
                                </div>
                                <div className="flex text-yellow-500">
                                    <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                                </div>
                            </div>
                            <p className="text-gray-700 italic leading-relaxed">"Excellent facilities, Coconut grove is beautiful. Moreover swimming pool is marvelous"</p>
                            <div className="mt-3 flex items-center text-sm text-blue-600">
                                <span className="bg-blue-100 px-2 py-1 rounded-full">Google Review</span>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-xl shadow-soft border-l-4 border-primary hover:shadow-medium transition-all duration-300 hover:-translate-y-1">
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <p className="font-bold text-gray-800">Kumar Rapat</p>
                                    <p className="text-sm text-gray-500">1 week ago</p>
                                </div>
                                <div className="flex text-yellow-500">
                                    <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                                </div>
                            </div>
                            <p className="text-gray-700 italic leading-relaxed">"Swimming pool is super"</p>
                            <div className="mt-3 flex items-center text-sm text-blue-600">
                                <span className="bg-blue-100 px-2 py-1 rounded-full">Google Review</span>
                            </div>
                        </div>
                    </div>

                    <div className="text-center bg-white mx-8 p-8 rounded-xl shadow-soft">
                        <h3 className="text-2xl font-bold text-gray-800 mb-6">Why Guests Love Us</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div className="text-center">
                                <div className="text-4xl mb-3">🏊‍♂️</div>
                                <p className="font-semibold text-primary">Marvelous Pool</p>
                            </div>
                            <div className="text-center">
                                <div className="text-4xl mb-3">🌴</div>
                                <p className="font-semibold text-primary">Coconut Grove</p>
                            </div>
                            <div className="text-center">
                                <div className="text-4xl mb-3">👨‍👩‍👧‍👦</div>
                                <p className="font-semibold text-primary">Family Safe</p>
                            </div>
                            <div className="text-center">
                                <div className="text-4xl mb-3">💰</div>
                                <p className="font-semibold text-primary">Great Value</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Location Section */}
                <section className="py-16">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold text-text mb-4">Find Us</h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">Located in the heart of Andhra Pradesh's beautiful countryside</p>
                    </div>
                    
                    <div className="bg-white rounded-2xl shadow-large overflow-hidden">
                        <div className="p-8 text-center border-b border-gray-200">
                            <h3 className="text-2xl font-bold text-text mb-4">Sunrise Agri Resorts</h3>
                            <p className="text-lg text-gray-600 mb-2">Bharthapuram (vill), bowdara(po), s.kota, Andhra Pradesh 535145, India</p>
                            <p className="text-gray-600">Easy access from major highways and cities</p>
                        </div>
                        <div className="aspect-[16/9] w-full">
                            <iframe
                                title="Sunrise Agri Resorts Location"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3791.044778062368!2d83.13147797557933!3d18.1618510800327!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a3bc5c03980bac1%3A0x92be252eef01bbf9!2sSunrise%20Agri%20Resorts!5e0!3m2!1sen!2sus!4v1766862077672!5m2!1sen!2sus"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                allowFullScreen={true}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                className="w-full h-full"
                            ></iframe>
                        </div>
                    </div>
                </section>

                {/* Call to Action */}
                <section className="py-16 text-center">
                    <div className="bg-gradient-to-r from-primary to-primary-dark rounded-3xl p-12 text-white">
                        <h2 className="text-4xl font-bold mb-4">Ready for Your Perfect Getaway?</h2>
                        <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
                            Book your stay at Sunrise Agri Resorts and experience the perfect blend of comfort, nature, and hospitality
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link 
                                to="/book-room" 
                                className="bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 shadow-medium hover:shadow-large"
                            >
                                Book Now
                            </Link>
                            <Link 
                                to="/contact" 
                                className="border-2 border-white text-white hover:bg-white hover:text-primary px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300"
                            >
                                Contact Us
                            </Link>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default Home;